import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import {
  useQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { PageHeader } from '@/components/PageHeader'
import { withField } from '@/components/reports/coverage-fields'
import { RtaAlertsTab } from '@/components/rtas/RtaAlertsTab'
import { RtaCoverageTab } from '@/components/rtas/RtaCoverageTab'
import { RtaDeleteButton } from '@/components/rtas/RtaDeleteButton'
import { RtaDeliveryTab } from '@/components/rtas/RtaDeliveryTab'
import { RtaEventList } from '@/components/rtas/RtaEventList'
import { RtaSetupChecklist } from '@/components/rtas/RtaSetupChecklist'
import { RtaTopicsTab } from '@/components/rtas/RtaTopicsTab'
import { timeAgo } from '@/components/rtas/rta-utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { accountQuery, coverageListsQuery, subaccountsQuery, topicsQuery } from '@/lib/queries'
import { rtaEventsQuery, rtaQuery, useUpdateRta } from '@/lib/rta-queries'

const TABS = ['alerts', 'topics', 'coverage', 'delivery', 'activity'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = {
  alerts: 'Alerts',
  topics: 'Topics',
  coverage: 'Coverage',
  delivery: 'Delivery',
  activity: 'Activity',
}

/**
 * One RTA — the sending assistant. Alerts is where an IA spends the day; Topics, Delivery and
 * Activity are its configuration and audit trail, on the same page so "sending and config live
 * together" (PRD). Tabs are search params like report config; `alert` names the open alert so a
 * Slack "View in Headline" link lands on it. Several RTAs at once are several session tabs, each
 * carrying its pending chip — the existing tab/breadcrumb setup rather than a multi-view page.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/rtas/$rtaId')({
  validateSearch: z.object({
    tab: z.enum(TABS).default('alerts').catch('alerts'),
    alert: z.uuid().optional().catch(undefined),
  }),
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.rta)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    const [subs] = await Promise.all([
      context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
      context.queryClient.ensureQueryData(rtaQuery(params.rtaId)),
      context.queryClient.ensureQueryData(coverageListsQuery(params.accountId)),
    ])
    // Every client's topics: an RTA may listen on any topic in its account, and the feed names them.
    await Promise.all(
      subs.subaccounts.map((sub) => context.queryClient.ensureQueryData(topicsQuery(sub.id))),
    )
  },
  component: RtaPage,
})

function RtaPage() {
  const { accountId, rtaId } = Route.useParams()
  const { tab, alert } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const { data: rta } = useSuspenseQuery(rtaQuery(rtaId))
  const { data: subsData } = useSuspenseQuery(subaccountsQuery(accountId))
  const { data: listsData } = useSuspenseQuery(coverageListsQuery(accountId))
  const topicResults = useSuspenseQueries({
    queries: subsData.subaccounts.map((sub) => topicsQuery(sub.id)),
  })
  const update = useUpdateRta(rtaId, queryClient)
  const events = useQuery({ ...rtaEventsQuery(rtaId), enabled: tab === 'activity' })
  const saveCoverage = (field: string, values: string[], refIds: string[]) =>
    update.mutate({ coverage: withField(rta.coverage, field, values, refIds) })

  const groups = subsData.subaccounts.map((sub, index) => ({
    subaccountId: sub.id,
    owner: sub.name,
    isPrimary: sub.isPrimary,
    topics: topicResults[index]?.data.topics ?? [],
  }))
  const topicNames = new Map(
    groups.flatMap((group) => group.topics.map((topic) => [topic.id, topic.name])),
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title={rta.name}
          description={`${rta.subaccountName} · ${rta.topicCount} ${rta.topicCount === 1 ? 'topic' : 'topics'} · matched through ${timeAgo(rta.matchedThrough)}`}
          className="mb-0"
        />
        <div className="flex items-center gap-2">
          <Badge variant={rta.status === 'active' ? 'default' : 'secondary'}>{rta.status}</Badge>
          <Button
            variant="outline"
            size="sm"
            disabled={update.isPending}
            onClick={() => update.mutate({ status: rta.status === 'active' ? 'paused' : 'active' })}
            title={
              rta.status === 'active'
                ? 'Stop matching new alerts. Existing alerts stay.'
                : 'Start matching again from where it left off.'
            }
          >
            {rta.status === 'active' ? 'Pause' : 'Resume'}
          </Button>
          <RtaDeleteButton rta={rta} />
        </div>
      </div>

      <RtaSetupChecklist rta={rta} accountId={accountId} />
      {update.isError && (
        <p role="alert" className="text-sm text-destructive">
          {update.error.message}
        </p>
      )}

      <nav aria-label="RTA tabs" className="flex gap-1 border-b border-border">
        {TABS.map((key) => (
          <Link
            key={key}
            from={Route.fullPath}
            search={{ tab: key }}
            aria-current={tab === key ? 'page' : undefined}
            className={
              tab === key
                ? '-mb-px border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {TAB_LABEL[key]}
            {key === 'alerts' && rta.pendingCount > 0 && (
              <Badge variant="warning" className="ml-1.5 px-1.5 py-0 tabular-nums">
                {rta.pendingCount}
              </Badge>
            )}
          </Link>
        ))}
      </nav>

      {tab === 'alerts' && (
        <RtaAlertsTab
          rta={rta}
          topicNames={topicNames}
          openAlertId={alert}
          onOpenAlert={(alertId) =>
            void navigate({ search: (prev) => ({ ...prev, alert: alertId }), replace: true })
          }
        />
      )}
      {tab === 'topics' && (
        <RtaTopicsTab rta={rta} accountId={accountId} groups={groups} lists={listsData.lists} />
      )}
      {tab === 'coverage' && (
        <RtaCoverageTab
          rta={rta}
          accountId={accountId}
          lists={listsData.lists}
          onCoverage={saveCoverage}
        />
      )}
      {tab === 'delivery' && <RtaDeliveryTab rta={rta} topicNames={topicNames} />}
      {tab === 'activity' && (
        <section className="rounded-md border border-border bg-card p-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Every change to this RTA and its alerts — who, what and when. Deletions and purges name
            what they removed.
          </p>
          <RtaEventList events={events.data?.events ?? []} />
        </section>
      )}
    </div>
  )
}
